
import React, { useState, useEffect } from "react";
import { useXero } from "@/contexts/XeroContext";
import { XeroSyncLog } from "./XeroSyncLog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { ExternalLink, Key, Check, X, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function XeroIntegration() {
  const { toast } = useToast();
  const { 
    isConnected, 
    connectionStatus, 
    lastSynced,
    connect, 
    disconnect, 
    syncData 
  } = useXero();
  
  const [isLoading, setIsLoading] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState(window.location.origin + "/xero-callback");
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncFrequency, setSyncFrequency] = useState("daily");

  // Update loading state based on connection status
  useEffect(() => {
    setIsLoading(connectionStatus === 'connecting');
  }, [connectionStatus]);

  // Update sync enabled state when connection status changes
  useEffect(() => {
    if (!isConnected) {
      setSyncEnabled(false);
    }
  }, [isConnected]);

  const handleConnect = async () => {
    if (!clientId || !clientSecret) {
      toast({
        title: "Missing credentials",
        description: "Please provide Xero API Client ID and Client Secret",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    await connect(clientId, clientSecret);
    setIsLoading(false);
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    await disconnect();
    setSyncEnabled(false);
    setIsLoading(false);
  };

  const handleSync = async () => {
    setIsLoading(true);
    await syncData();
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold mb-2">Xero Integration</h2>
        <p className="text-muted-foreground mb-4">
          Integrate with Xero to synchronize your financial and accounting data. This integration allows you to:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1 text-muted-foreground">
          <li>Import invoices and expenses from Xero</li>
          <li>Export resource plans and valuations to Xero</li>
          <li>Keep your financial data in sync across systems</li>
          <li>Automate your financial reporting</li>
        </ul>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Xero Connection</CardTitle>
          <CardDescription>
            Connect with Xero to sync your accounting data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Connection Status</h3>
              <p className="text-sm text-muted-foreground">
                {isConnected ? "Connected to Xero" : "Not connected"}
              </p>
            </div>
            <div className="flex items-center">
              {isConnected ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 flex items-center">
                    <Check className="h-4 w-4 mr-1" /> Connected
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDisconnect}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disconnect"}
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handleConnect}
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Connect to Xero
                </Button>
              )}
            </div>
          </div>

          {!isConnected && (
            <div className="space-y-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-id">Xero API Client ID</Label>
                  <div className="flex gap-2">
                    <Input 
                      id="client-id" 
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="Enter your Xero API Client ID"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="client-secret">Xero API Client Secret</Label>
                  <div className="flex gap-2">
                    <Input 
                      id="client-secret" 
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="Enter your Xero API Client Secret"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="redirect-uri">Redirect URI</Label>
                  <div className="flex gap-2">
                    <Input 
                      id="redirect-uri" 
                      value={redirectUri}
                      onChange={(e) => setRedirectUri(e.target.value)}
                      placeholder="https://your-app.com/xero-callback"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This should match the redirect URI configured in your Xero Developer account
                  </p>
                </div>
              </div>
              
              <Alert>
                <Key className="h-4 w-4" />
                <AlertTitle>How to get your API credentials</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">To obtain your Xero API credentials:</p>
                  <ol className="list-decimal ml-5 space-y-1 text-sm">
                    <li>Log in to your <a href="https://developer.xero.com/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Xero Developer account <ExternalLink className="h-3 w-3 inline" /></a></li>
                    <li>Create a new app or select an existing one</li>
                    <li>Configure the redirect URI to match the one above</li>
                    <li>Copy the Client ID and Client Secret</li>
                  </ol>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {isConnected && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Automatic Synchronization</h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync data with Xero
                  </p>
                </div>
                <Switch 
                  checked={syncEnabled} 
                  onCheckedChange={setSyncEnabled} 
                />
              </div>
              
              {syncEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="sync-frequency">Sync Frequency</Label>
                  <select 
                    id="sync-frequency"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={syncFrequency}
                    onChange={(e) => setSyncFrequency(e.target.value)}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              )}
              
              <div className="pt-2">
                <Button 
                  variant="outline"
                  disabled={isLoading}
                  onClick={handleSync}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Sync Now
                </Button>
                {lastSynced && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Last synced: {lastSynced.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isConnected && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Data Mapping</CardTitle>
              <CardDescription>
                Configure how your data is mapped between systems
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Mapping Options</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="map-categories" />
                      <label
                        htmlFor="map-categories"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Map expense categories to Xero accounts
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="map-projects" />
                      <label
                        htmlFor="map-projects"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Map projects to Xero tracking categories
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="map-contacts" />
                      <label
                        htmlFor="map-contacts"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Sync contacts with Xero
                      </label>
                    </div>
                  </div>
                </div>
                
                <Button variant="secondary" className="mt-4">
                  Configure Advanced Mapping
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <div className="mt-6">
            <XeroSyncLog />
          </div>
        </>
      )}
    </div>
  );
}

// Missing Checkbox component import
import { Checkbox } from "@/components/ui/checkbox";
